import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown, Badge, Spinner } from 'reactstrap';
import axios from 'axios';
import { useAuth } from '../../../Provider/AuthProvider';

// Interface pour les produits en alerte depuis l'API
interface ProductAlert {
    rowid: string;
    ref: string;
    ref_ext: string | null;
    label: string;
    seuil_stock_alerte: number | null;
    stock_quantity: number;
    fk_entrepot: string;
    entrepot_ref: string;
    entrepot_lieu: string;
    entrepot_id: string;
    alert_level: 'alerte_stock_faible' | 'rupture_stock' | 'alerte_stock';
    stock_deficit: number | null;
    alert_percentage: number;
    stock_status_label: string;
}

interface ApiResponse {
    success: boolean;
    message: string;
    data: ProductAlert[];
    nbtotalofrecords: number;
    totalpages: number;
    count: number;
    applied_filter?: {
        search_status_input?: string;
        search_status_code?: string;
        search_status_label?: string;
    };
}

// Mapping des filtres UI vers les codes API
const FILTER_MAPPING = {
    "Tous les statuts": {
        label: "Tous les statuts",
        apiValue: ""
    },
    "Stock faible": {
        label: "Stock faible",
        apiValue: "alerte_stock_faible"
    },
    "Rupture de stock": {
        label: "Rupture de stock",
        apiValue: "rupture_stock"
    },
    "Stock en alerte": {
        label: "Stock en alerte",
        apiValue: "alerte_stock"
    }
} as const;

type FilterKey = keyof typeof FILTER_MAPPING;

const StockAlerts = () => {
    const { user } = useAuth();
    const [selectedFilter, setSelectedFilter] = useState<FilterKey>("Tous les statuts");
    const [searchStatus, setSearchStatus] = useState<string>("");
    const [products, setProducts] = useState<ProductAlert[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [appliedFilter, setAppliedFilter] = useState<any>(null);
    const [pagination, setPagination] = useState({
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1,
        limit: 100
    });

    // Mettre à jour searchStatus quand selectedFilter change
    useEffect(() => {
        setSearchStatus(FILTER_MAPPING[selectedFilter].apiValue);
        setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset à la page 1
    }, [selectedFilter]);

    // Fonction pour charger les données depuis l'API
    const fetchStockAlerts = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Vérification de l'authentification
            if (!user?.token) {
                console.error('Utilisateur non authentifié - token manquant');
                setLoading(false);
                return;
            }

            if (!user?.entity) {
                console.error('Entité utilisateur manquante');
                setLoading(false);
                return;
            }

            // Construction du payload
            const payload: any = {
                action: 'stockalert',
                entity: user.entity,
                page: pagination.currentPage,
                limit: pagination.limit,
                sortfield: 'stock_quantity',
                sortorder: 'ASC'
            };

            // Ajouter search_status seulement si un filtre est sélectionné
            if (searchStatus) {
                payload.search_status = searchStatus;
            }

            console.log('Envoi de la requête stock alert avec:', {
                url: 'https://api.apapiecesauto.fr/apiV2/',
                payload,
                token: user.token ? 'Présent' : 'Manquant'
            });

            const response: any = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            // Vérifier que la réponse est valide
            if (response.data && response.success === true) {
                const fetchedProducts = response.data || [];
                
                setProducts(fetchedProducts);
                setPagination(prev => ({
                    ...prev,
                    totalRecords: response.nbtotalofrecords || 0,
                    totalPages: response.totalpages || 0
                }));
                setAppliedFilter(response.applied_filter || null);
                
                console.log('Produits en alerte récupérés:', fetchedProducts);
            } else {
                console.warn('La réponse API ne contient pas success=true', response.data);
                setProducts([]);
                setPagination(prev => ({
                    ...prev,
                    totalRecords: 0,
                    totalPages: 0
                }));
                setError(response.data?.message || 'Erreur lors du chargement des données');
            }
        } catch (error: any) {
            console.error('Erreur lors de la récupération des produits en alerte:', error);
            
            if (error.response) {
                console.error('Détails de l\'erreur:', {
                    status: error.response.status,
                    data: error.response.data,
                    headers: error.response.headers
                });

                if (error.response.status === 401) {
                    setError('Session expirée - veuillez vous reconnecter');
                } else if (error.response.status === 403) {
                    setError('Accès non autorisé à cette ressource');
                } else if (error.response.status === 500) {
                    setError('Erreur serveur - veuillez réessayer plus tard');
                } else {
                    setError(error.response.data?.message || `Erreur ${error.response.status}`);
                }
            } else if (error.request) {
                console.error('Aucune réponse reçue du serveur:', error.request);
                setError('Impossible de contacter le serveur');
            } else {
                console.error('Erreur de configuration:', error.message);
                setError('Erreur de configuration de la requête');
            }
            
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.token) {
            fetchStockAlerts();
        } else {
            console.log('En attente de l\'authentification...');
            setLoading(false);
        }
    }, [user?.token, user?.entity, pagination.currentPage, searchStatus]);

    // Fonction pour tronquer le texte
    const truncateText = (text: string, maxLength: number) => {
        if (!text) return '';
        return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    };

    // Fonction pour obtenir le badge de statut
    const getStatusBadge = (status: string) => {
        switch(status) {
            case 'rupture_stock':
                return <Badge color="danger" className="bg-opacity-10 text-danger">Rupture de stock</Badge>;
            case 'alerte_stock_faible':
                return <Badge color="warning" className="bg-opacity-10 text-warning">Stock faible</Badge>;
            case 'alerte_stock':
                return <Badge color="info" className="bg-opacity-10 text-info">Stock en alerte</Badge>;
            default:
                return null;
        }
    };

    // Fonction pour obtenir l'icône de statut
    const getStatusIcon = (status: string) => {
        switch(status) {
            case 'rupture_stock':
                return 'ri-alert-line text-danger';
            case 'alerte_stock_faible':
                return 'ri-alert-line text-warning';
            case 'alerte_stock':
                return 'ri-information-line text-info';
            default:
                return 'ri-checkbox-circle-line';
        }
    };

    // Styles inline pour le tableau avec hauteur fixe
    const tableContainerStyle = {
        maxHeight: '400px',
        overflowY: 'auto' as const,
        scrollbarWidth: 'thin' as const,
        msOverflowStyle: 'auto' as const
    };

    const tableHeadStyle = {
        position: 'sticky' as const,
        top: 0,
        zIndex: 1,
        backgroundColor: '#f8f9fa'
    };

    const formatNumber = (num: number) => {
        return num?.toLocaleString() || '0';
    };

    // Vérification si l'utilisateur est authentifié
    if (!user?.token) {
        return (
            <Col xl={12}>
                <Card>
                    <CardBody>
                        <div className="text-center py-4">
                            <i className="ri-error-warning-line display-5 text-warning"></i>
                            <h5 className="mt-3">Authentification requise</h5>
                            <p className="text-muted">
                                Veuillez vous connecter pour voir les alertes de stock
                            </p>
                        </div>
                    </CardBody>
                </Card>
            </Col>
        );
    }

    // Affichage en cas d'erreur
    if (error) {
        return (
            <Col xl={6}>
                <Card>
                    <CardBody>
                        <div className="text-center py-4">
                            <i className="ri-error-warning-line display-5 text-danger"></i>
                            <h5 className="mt-3 text-danger">Erreur</h5>
                            <p className="text-muted">{error}</p>
                        </div>
                    </CardBody>
                </Card>
            </Col>
        );
    }

    return (
        <Col xl={12}>
            <Card className="card-height-100">
                <CardHeader className="align-items-center d-flex">
                    <h4 className="card-title mb-0 flex-grow-1">Alertes Stock</h4>
                    <div className="flex-shrink-0">
                        <UncontrolledDropdown className="card-header-dropdown">
                            <DropdownToggle tag="a" className="text-reset dropdown-btn" role="button">
                                <span className="fw-semibold text-uppercase fs-12">Filtre: </span>
                                <span className="text-muted">
                                    {FILTER_MAPPING[selectedFilter].label} <i className="mdi mdi-chevron-down ms-1"></i>
                                </span>
                            </DropdownToggle>
                            <DropdownMenu className="dropdown-menu-end">
                                {(Object.keys(FILTER_MAPPING) as FilterKey[]).map((key) => (
                                    <DropdownItem 
                                        key={key}
                                        onClick={() => setSelectedFilter(key)}
                                        active={selectedFilter === key}
                                    >
                                        {FILTER_MAPPING[key].label}
                                    </DropdownItem>
                                ))}
                            </DropdownMenu>
                        </UncontrolledDropdown>
                    </div>
                </CardHeader>

                <CardBody>
                   

                    {loading && (
                        <div className="text-center py-4">
                            <Spinner color="primary">
                                Chargement...
                            </Spinner>
                        </div>
                    )}
                    
                    {!loading && products.length > 0 && (
                        <div className="table-responsive" style={tableContainerStyle}>
                            <table className="table table-centered table-hover align-middle table-nowrap mb-0">
                                <thead className="text-muted table-light" style={tableHeadStyle}>
                                    <tr>
                                        <th scope="col">Produit</th>
                                        <th scope="col">Référence</th>
                                        <th scope="col">Stock</th>
                                        <th scope="col">Statut</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map((product) => (
                                        <tr key={product.rowid}>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className="flex-shrink-0 me-2">
                                                        <div className="avatar-sm">
                                                            <div className="avatar-title bg-light rounded-circle">
                                                                <i className="ri-shopping-bag-line text-primary"></i>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <h5 className="fs-14 my-1 fw-medium">
                                                            <Link 
                                                                to={`/apps-ecommerce-product-details/${product.rowid}`} 
                                                                className="text-reset"
                                                                title={product.label}
                                                            >
                                                                {truncateText(product.label, 20)}
                                                            </Link>
                                                        </h5>
                                                        <small className="text-muted d-block">
                                                            Seuil: {product.seuil_stock_alerte || 0}
                                                        </small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="fw-medium">{product.ref}</span>
                                                {product.ref_ext && (
                                                    <small className="text-muted d-block">
                                                        Ext: {product.ref_ext}
                                                    </small>
                                                )}
                                            </td>
                                            <td>
                                                <div>
                                                    <h5 className={`fs-14 my-1 fw-${product.stock_quantity < 0 ? 'bold text-danger' : 'normal'}`}>
                                                        {formatNumber(product.stock_quantity)} pcs
                                                    </h5>
                                                   
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <i className={getStatusIcon(product.alert_level)}></i>
                                                    {getStatusBadge(product.alert_level)}
                                                </div>
                                               
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    
                    {!loading && products.length === 0 && (
                        <div className="text-center py-4">
                            <i className="ri-stock-line display-5 text-muted"></i>
                            <p className="text-muted mb-0 mt-3">
                                {searchStatus 
                                    ? `Aucun produit en ${FILTER_MAPPING[selectedFilter].label.toLowerCase()}`
                                    : "Aucun produit en alerte de stock"}
                            </p>
                        </div>
                    )}
                    
                    {!loading && products.length > 0 && (
                        <div className="mt-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                    Affichage de {products.length} produit(s) sur {pagination.totalRecords} en alerte
                                </small>
                                <div className="d-flex gap-2">
                                    <Badge color="danger" className="bg-opacity-10 text-danger" pill>
                                        {products.filter(p => p.alert_level === 'rupture_stock').length} Rupture
                                    </Badge>
                                    <Badge color="warning" className="bg-opacity-10 text-warning" pill>
                                        {products.filter(p => p.alert_level === 'alerte_stock_faible').length} Faible
                                    </Badge>
                                    <Badge color="info" className="bg-opacity-10 text-info" pill>
                                        {products.filter(p => p.alert_level === 'alerte_stock').length} Alerte
                                    </Badge>
                                </div>
                            </div>
                            
                            {/* Pagination simplifiée */}
                            {pagination.totalPages > 1 && (
                                <div className="d-flex justify-content-center mt-3">
                                    <div className="pagination">
                                        <button 
                                            className="btn btn-sm btn-primary me-2"
                                            onClick={() => setPagination(prev => ({...prev, currentPage: Math.max(1, prev.currentPage - 1)}))}
                                            disabled={pagination.currentPage === 1}
                                        >
                                            <i className="ri-arrow-left-line"></i>
                                        </button>
                                        <span className="align-self-center">
                                            Page {pagination.currentPage} / {pagination.totalPages}
                                        </span>
                                        <button 
                                            className="btn btn-sm btn-primary ms-2"
                                            onClick={() => setPagination(prev => ({...prev, currentPage: Math.min(pagination.totalPages, prev.currentPage + 1)}))}
                                            disabled={pagination.currentPage === pagination.totalPages}
                                        >
                                            <i className="ri-arrow-right-line"></i>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </CardBody>
            </Card>
        </Col>
    );
};

export default StockAlerts;